/**
 * answer-edit-comments.js
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import analytics from 'utils/analytics';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { Types } from 'utils/constants';
import useRecordStore from 'contexts/record-store';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import EditorTextTemplates from 'components/editor-text-templates';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import TextField from 'components/form-inputs/text-field';
import OdorSummary from '../odor-summary';


const useStyles = makeStyles(theme => ({
  exerSummaryContainer: {
    marginTop: theme.spacing(1),
    fontWeight: theme.typography.fontWeightLight,
    [theme.breakpoints.down('sm_md')]: {
      marginTop: 0,
    },
    '& .MuiTypography-paragraph': {
      marginBottom: theme.spacing(1),
    },
  },
  exerSummary: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightLight,
  },
  exerSummaryTitle: {
    color: theme.palette.primary.main,
    [theme.breakpoints.down('sm_md')]: {
      marginTop: theme.spacing(0.25),
    }
  },
  goal: {
    fontWeight: theme.typography.fontWeightMedium
  }
}));


function AnswerEditSummary(props) {
  const { drawerState, storedRecord, exerciseId, translation } = props;
  const classes = useStyles();
  const { t } = useTranslation(translation);
  const recordStore = useRecordStore();
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId).ornull;
  const exercise = exerciseRecord.get();
  if (!exercise) {
    return null;
  }

  const goal = exercise.goal;
  const workMode = exercise.workMode;
  const detectionEnvironments = storedRecord.detection?.environments;
  
  return (
    <HelpTextItem
      sidebarText={drawerState.sidebarText}
      title={t('comments.exerciseSummary.helpTitle')}
      text={t('comments.exerciseSummary.helpInfo')}
      className={classes.exerSummaryContainer}
    >
      <EditorAccordion
        highlight
        name="Exercise Summary"
        className={classes.exerSummary}
        summary={
          <FormSectionTitle>
            Exercise Summary (for review only)
          </FormSectionTitle>}
        detail={
          <Box py={0}>
            <Typography
              paragraph
              variant="body2"
            >
              <span className={classes.goal}>GOAL: </span>{goal}
            </Typography>
            {workMode === Types.WorkMode.DETECTION && detectionEnvironments.length > 0 &&
              <Box mt={2}>
                <Typography paragraph variant="subtitle2">
                  ODOR SUMMARY:
                </Typography>
                <OdorSummary detectionEnvironments={detectionEnvironments} detailed />
              </Box>}
          </Box>
        }
      />
    </HelpTextItem>    
  );
}


export default function AnswerEditComments(props) {
  const {
    storedRecord,
    exerciseId,
    drawerState,
    translation,
    options,
    workModeName,
    formPrefix = '',
  } = props;
  const { t } = useTranslation(translation);
  const { control, getValues, setValue } = useFormContext();
  const wasPerformedValue = useWatch({ control, name: `${formPrefix}wasExercisePerformed` });


  return <>
    {wasPerformedValue &&
    <>
      <EditorIconSection
        iconName='comment'
        iconMarginTop={26}
        helpProps={{
          translation,
          section: 'comments'
        }}
      >
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('comments.handlerComments.helpTitle')}
          text={t('comments.handlerComments.helpInfo')}
        >
          {focusHandlers =>
            <>
              <EditorTextTemplates
                recent={options.recentEntries.comments}
                translation={translation}
                samplesTranslationKey={`templates.${workModeName}.comments.samples`}
                onSelect={value => {
                  analytics.trackGA4('event', { name: 'record_template', params: { record_type: 'completion', template_type: 'comments' } });
                  const currValue = getValues(`${formPrefix}comments`);
                  const newValue = `${currValue}${currValue ? '\n\n' : ''}${value}`;
                  setValue(`${formPrefix}comments`, newValue, { shouldDirty: true });
                }}
              />
              <TextField
                name={`${formPrefix}comments`}
                label={t('comments.handlerComments.label')}
                focusHandlers={focusHandlers}
                rules={{
                  required: t('comments.handlerComments.errMsgEmpty'),
                  pattern: {
                    value: /.*\S+.*/,
                    message: t('comments.handlerComments.errMsgEmpty')
                  }
                }}
                multiline
                rows={shouldRenderMobileLayout() ? 12 : 8}
              />
            </>}
        </HelpTextItem>

        {exerciseId &&
          <AnswerEditSummary
            {...{ drawerState, storedRecord, exerciseId, translation }}
          />
        }
      </EditorIconSection>
    </>}
  </>;
}